import clsx from 'clsx'
import debounce from 'lodash/debounce'
import minBy from 'lodash/minBy'
import React, { FC, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'

import { HorizontalCarouselLeftArrow } from '@Shared/Components/Carousels/HorizontalCarousel/Components/HorizontalCarouselLeftArrow'
import { HorizontalCarouselProgressBar } from '@Shared/Components/Carousels/HorizontalCarousel/Components/HorizontalCarouselProgressBar'
import { HorizontalCarouselRightArrow } from '@Shared/Components/Carousels/HorizontalCarousel/Components/HorizontalCarouselRightArrow'

import styles from './HorizontalCarousel.module.scss'

export interface HorizontalCarouselProps {
  alwaysShowButtons?: boolean
  alwaysShowProgressBar?: boolean
  buttonClassName?: string
  buttonFloat?: boolean
  buttonMoveOneField?: boolean
  buttonsCarouselClass?: string
  carouselClassName?: string
  children?: React.ReactNode
  className?: string
  gap?: number
  halfSizeProgressBar?: boolean
  progressClassName?: string
  showButtons?: boolean
  showProgressbar?: boolean
  snap?: boolean
  unevenChildWidth?: boolean
}

export const HorizontalCarousel: FC<HorizontalCarouselProps> = ({
  alwaysShowButtons = false,
  alwaysShowProgressBar,
  buttonClassName,
  buttonFloat = false,
  buttonMoveOneField = false,
  buttonsCarouselClass,
  carouselClassName,
  children,
  className,
  gap = 2,
  halfSizeProgressBar = false,
  progressClassName,
  showButtons,
  showProgressbar = false,
  snap = false,
  unevenChildWidth,
}) => {
  const [isDown, setIsDown] = useState<boolean>(false)
  const [isDragged, setIsDragged] = useState<boolean>(false)
  const [mouseDownScrollLeft, setMouseDownMouseDownScrollLeft] = useState<number>(0)
  const [startX, setStartX] = useState<number>(0)
  const [active, setActive] = useState<boolean>(false)
  const [percentage, setPercentage] = useState<number>(0)
  const [canShowProgressbar, setCanShowProgressbar] = useState(false)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const updateOnResize = debounce(() => {
      if (!ref.current) {
        return
      }
      const show = ref.current.scrollWidth > ref.current.clientWidth
      if (showButtons) {
        setShowRightArrow(show)
      }
      if (showProgressbar) {
        setCanShowProgressbar(show)
      }
    }, 100)
    if (showButtons || showProgressbar) {
      updateOnResize()
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', updateOnResize, { passive: true })
      }
    }
    return () => window.removeEventListener('resize', updateOnResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const scrollToNearestChild = (
    sliderRef: MutableRefObject<HTMLDivElement | null>,
    modifier?: boolean,
  ) => {
    if (!sliderRef.current) {
      return
    }
    const {
      children: sliderChildren,
      clientWidth: viewPortWidth,
      scrollLeft: slideLeft,
      scrollWidth,
    } = sliderRef.current
    let scrollLeft = slideLeft
    if (modifier) {
      scrollLeft += viewPortWidth
    }
    if (!sliderChildren || !sliderChildren.length) {
      return
    }
    const getNearest = (child: Element) => Math.abs((child as HTMLElement).offsetLeft - scrollLeft)
    const nearestChild = minBy(sliderChildren, getNearest) as HTMLElement
    if (scrollWidth === slideLeft + viewPortWidth) {
      sliderRef.current.scrollTo({
        left: scrollWidth,
        behavior: 'smooth',
      })
      return
    }
    if (nearestChild) {
      const { offsetLeft: left } = nearestChild
      sliderRef.current.scrollTo({
        left,
        behavior: 'smooth',
      })
    }
  }

  const updatePercentage = (event) => {
    const eScrollLeft = event.target.scrollLeft || 0
    const eScrollWidth = event.target.scrollWidth || 0
    const eClientWidth = event.target.clientWidth || 0
    setShowRightArrow(!(Math.round(eScrollLeft) >= (eScrollWidth - eClientWidth)))
    setShowLeftArrow(eScrollLeft > 0)
    const diff = eScrollWidth - eClientWidth
    setPercentage(Math.round((eScrollLeft / diff) * 100))
  }

  const preventClick = useCallback((e) => {
    e.preventDefault()
    e.stopImmediatePropagation()
  }, [])

  const onMouseDown = (e) => {
    e.stopPropagation()
    e.preventDefault()
    setIsDown(true)
    setStartX(e.pageX - ref.current!.offsetLeft)
    setMouseDownMouseDownScrollLeft(ref.current!.scrollLeft)
  }
  const onMouseLeave = (e) => {
    e.stopPropagation()
    e.preventDefault()
    setIsDown(false)
    setActive(false)
    scrollToNearestChild(ref)
  }

  const onMouseMove = (e) => {
    if (!isDown && !active) {
      return
    }
    if (!isDown && active) {
      setActive(false)
      return
    }
    e.stopPropagation()
    e.preventDefault()
    setActive(true)
    setIsDragged(true)
    setActive(true)
    const x = e.pageX - ref.current!.offsetLeft
    const walk = (x - startX)
    ref.current!.scrollLeft = mouseDownScrollLeft - walk
  }
  const onMouseUp = (e) => {
    if (!ref.current) {
      return
    }
    e.stopPropagation()
    e.preventDefault()
    setIsDown(false)
    if (isDragged) {
      ref.current!.addEventListener('click', preventClick)
    } else {
      ref.current!.removeEventListener('click', preventClick)
    }
    setIsDragged(false)
    setActive(false)
    scrollToNearestChild(ref)
  }

  const scrollGreaterThenWidth = ref?.current?.scrollWidth && ref?.current?.clientWidth && ref?.current?.scrollWidth > ref?.current?.clientWidth

  return (
    <div className={clsx(styles.carouselWrapper, className)}>
      <div className={clsx(styles.horizontalCarousel, buttonsCarouselClass)}>
        {((alwaysShowButtons || (showLeftArrow && showButtons)) && scrollGreaterThenWidth) && (
          <HorizontalCarouselLeftArrow
            buttonClassName={clsx(buttonClassName, alwaysShowButtons && !showLeftArrow && styles.disableButton)}
            buttonFloat={buttonFloat}
            buttonMoveOneField={buttonMoveOneField}
            carouselRef={ref}
            gap={gap}
            unevenChildWidth={unevenChildWidth}
          />
        )}
        <div
          className={clsx(
            styles.carouselWrapper,
            active && styles.active,
            snap && styles.snap,
            carouselClassName,
          )}
          onMouseDown={e => onMouseDown(e)}
          onMouseLeave={e => onMouseLeave(e)}
          onMouseMove={e => onMouseMove(e)}
          onMouseUp={e => onMouseUp(e)}
          onScroll={e => updatePercentage(e)}
          ref={ref}
          style={({
            gap: `${gap}px`,
          })}
        >
          {children}
        </div>
        {((alwaysShowButtons || (showButtons && showRightArrow && scrollGreaterThenWidth)) && scrollGreaterThenWidth) && (
          <HorizontalCarouselRightArrow
            buttonClassName={clsx(buttonClassName, alwaysShowButtons && !showRightArrow && styles.disableButton)}
            buttonFloat={buttonFloat}
            buttonMoveOneField={buttonMoveOneField}
            carouselRef={ref}
            gap={gap}
            unevenChildWidth={unevenChildWidth}
          />
        )}
      </div>
      {showProgressbar && (
        <div className={styles.carouselProgressWrapper}>
          {((canShowProgressbar && scrollGreaterThenWidth) || alwaysShowProgressBar) && (
            <HorizontalCarouselProgressBar
              carouselRef={ref}
              halfSizeProgressBar={halfSizeProgressBar}
              onPercentageChange={setPercentage}
              percentage={percentage}
              progressClassName={progressClassName}
              setActive={setActive}
            />
          )}
        </div>
      )}

    </div>
  )
}
